// BUILD-046 cycle C (Edit 3): what makes an active Customer filter obvious while the Filter popover is closed.
//
//   CustomerFilterChip       a removable chip (since 1b-ii INLINE in the toolbar's wrapping row, beside the other selected-filter chips): "Customer: Smith Residence" (or "Customer: No customer") with a Remove button named
//                       "Remove Customer filter". There is no chip for All customers. Removing it clears ONLY the customer filter (customerFilterModel.removeCustomerFilter; the status,
//                       department, search, sort and density are not touched). Focus moves to the Filter button first, so a keyboard user is never dropped onto the page,
//                       and a polite line says the filter was removed.
//   CustomerFilterEmptyState the truthful empty state for a chosen customer that leaves no rows: it names the customer filter (and the status and other filters that also apply) and
//                       offers to clear the customer filter. It is never the generic "no tasks".
// The wording is in customerFilterModel.ts (describeCustomerFilterChip, describeCustomerFilterEmpty); this file draws it.
import { useEffect, useState } from 'react';
import { describeCustomerFilterChip } from './customerFilterModel';
import type { CustomerFilter, CustomerFilterEmptyView } from './customerFilterModel';

export interface CustomerFilterChipProps {
  filter: CustomerFilter;
  onRemove: () => void;
}

/** The Filter button if it is on the page, else the toolbar's search box: where focus goes when the chip it was on disappears. */
export function focusToolbar(): void {
  const target = document.querySelector<HTMLElement>('[data-testid="toolbar-filter"]') ?? document.querySelector<HTMLElement>('[data-testid="work-toolbar"] input[type="search"]');
  target?.focus();
}

export function CustomerFilterChip({ filter, onRemove }: CustomerFilterChipProps) {
  const chip = describeCustomerFilterChip(filter);
  const [removed, setRemoved] = useState(false);
  const active = chip !== null;
  useEffect(() => {
    if (active) setRemoved(false);
  }, [active]);
  return (
    // 1b-ii: the chip sits INLINE in the toolbar's wrapping row (display: contents), no longer in a block of its own between the toolbar and the table.
    <div className="contents" data-testid="customer-filter-chip-area">
      {/* Always on the page, so the removal is announced after the chip itself is gone. */}
      <div role="status" aria-live="polite" className="sr-only">
        {!active && removed ? 'Customer filter removed. Showing all customers.' : ''}
      </div>
      {chip !== null && (
        <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-sky-200 bg-sky-50 py-0.5 pl-3 pr-1 text-xs text-gray-900" data-testid="customer-filter-chip">
          <span className="min-w-0 break-words">{chip.text}</span>
          <button
            type="button"
            aria-label={chip.removeLabel}
            title={chip.removeLabel}
            onClick={() => {
              focusToolbar();
              setRemoved(true);
              onRemove();
            }}
            className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-gray-700 hover:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </span>
      )}
    </div>
  );
}

export interface CustomerFilterEmptyStateProps {
  view: CustomerFilterEmptyView;
  onClear: () => void;
}

export function CustomerFilterEmptyState({ view, onClear }: CustomerFilterEmptyStateProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600" data-testid="customer-filter-empty">
      <p role="status" className="font-medium text-gray-800">
        {view.headline}
      </p>
      <p className="mt-1">{view.detail}</p>
      <button
        type="button"
        onClick={() => {
          focusToolbar();
          onClear();
        }}
        className="mt-3 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-[#1f6a99] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
      >
        {view.clearLabel}
      </button>
    </div>
  );
}
